import { getElearningProductName } from '../features/dashboard/elearningProductNames'
import { resolveContentfulResponse, rewriteUrl } from './contentfulUtils'
import {
  buildProductDisplayLookup,
  buildProductDisplayOrderIndex,
  resolveProductIconUrl,
  sortEnrichedDrawerProducts,
  applyDrawerProductGroups,
  DRAWER_PRODUCT_GROUPS,
  mergeDrawerProductsWithDisplayConfig as mergeDrawerProducts,
  type ProductDisplayEntry,
  type DrawerProductGroupItem,
} from 'prosci-ui'

export interface DrawerProduct {
  id: string
  name: string
  code: string
  sort?: number | null
  accessUrl?: string | null
  productTag?: string | null
  isKnowledgeHub?: boolean
  isResearchHub?: boolean
  isInstructorHub?: boolean
  isPrework?: boolean
  isProxima?: boolean
  expirationDate?: string | null
  hasRenewableSubscription?: boolean
}

export interface EnrichedDrawerProduct extends DrawerProduct {
  displayName: string
  iconUrl?: string
  /** Present only on a synthetic group tile (e.g. "eLearning"); opens a drill-down modal instead of navigating. */
  items?: DrawerProductGroupItem[]
  itemsSubtitle?: string
}

interface ResolvedPortalDrawer {
  // `resolveContentfulResponse` only flattens the top-level items array, so
  // nested `productDisplays` links are resolved by `contentful-resolve-response`
  // but left in raw Contentful entry shape (`{ sys, fields }`), not flattened
  // to `{ productCode, displayName, icon }` like the top-level item is.
  productDisplays?: Array<{ fields?: ProductDisplayEntry } | ProductDisplayEntry>
  productCode?: string
}

export { buildProductDisplayLookup, buildProductDisplayOrderIndex, resolveProductIconUrl, sortEnrichedDrawerProducts }

export function extractOrderedDisplayEntries(
  contentfulResponse: { items?: unknown[] } | null | undefined
): ProductDisplayEntry[] {
  if (!contentfulResponse?.items?.length) return []

  const resolved = resolveContentfulResponse(
    contentfulResponse
  ) as ResolvedPortalDrawer[]
  const config = resolved[0]

  if (config?.productDisplays?.length) {
    return config.productDisplays
      .map((display) => ('fields' in display ? display.fields : display))
      .filter((entry): entry is ProductDisplayEntry => Boolean(entry?.productCode))
  }

  return resolved.filter((entry) => entry.productCode)
}

export function resolveProductDisplayName(
  product: DrawerProduct,
  cmsEntry?: ProductDisplayEntry,
  locale?: string
): string {
  if (!cmsEntry && import.meta.env.DEV) {
    console.warn(`No portalProductDisplay entry for entitled product code: ${product.code}`)
  }
  if (cmsEntry?.displayName) return cmsEntry.displayName
  const elearningName = getElearningProductName(product.code, locale)
  if (elearningName) return elearningName
  return product.name
}

export function mergeDrawerProductsWithDisplayConfig(
  products: DrawerProduct[],
  contentfulResponse: { items?: unknown[] } | null | undefined,
  locale?: string
): EnrichedDrawerProduct[] {
  return applyDrawerProductGroups(
    enrichDrawerProductsWithDisplayConfig(products, contentfulResponse, locale),
    DRAWER_PRODUCT_GROUPS
  )
}

/**
 * Individual (ungrouped) products in the exact order the product drawer shows them:
 * each group tile is expanded back into its member products at the group's position.
 */
export function orderDrawerProductsLikeDrawer(
  products: DrawerProduct[],
  contentfulResponse: { items?: unknown[] } | null | undefined,
  locale?: string
): EnrichedDrawerProduct[] {
  const enriched = enrichDrawerProductsWithDisplayConfig(products, contentfulResponse, locale)
  const enrichedById = new Map(enriched.map((product) => [String(product.id), product]))

  return applyDrawerProductGroups(enriched, DRAWER_PRODUCT_GROUPS).flatMap((product) =>
    product.items
      ? product.items.flatMap((item) => enrichedById.get(String(item.id)) ?? [])
      : [product]
  )
}

export function enrichDrawerProductsWithDisplayConfig(
  products: DrawerProduct[],
  contentfulResponse: { items?: unknown[] } | null | undefined,
  locale?: string
): EnrichedDrawerProduct[] {
  const orderedDisplays = extractOrderedDisplayEntries(contentfulResponse)
  return mergeDrawerProducts(
    products,
    orderedDisplays,
    (product, cmsEntry) => resolveProductDisplayName(product, cmsEntry, locale),
    rewriteUrl
  )
}
